import { createSelectItemKey } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { measureElement as defaultMeasureElement } from '@tanstack/virtual-core';
import { useCallback, useMemo, useRef, type RefObject } from 'react';

export type VirtualRowKind = 'divider' | 'selected' | 'notSelected' | 'category';

export interface VirtualRowMeta<T> {
  kind: VirtualRowKind;
  option?: SelectItem<T>;
  category?: string;
  indexWithinGroup?: number;
  isNoCategory?: boolean;
  isFirstCategory?: boolean;
}

export interface UseVirtualizedOptionsParams<T> {
  selectedOptions: SelectItem<T>[];
  notSelectedOptions: SelectItem<T>[];
  hasCategorizedOptions: boolean;
  categoriesToDisplay: Array<{ category: string; index: number }>;
  includeDivider: boolean;
  noCategoryLabel: string;
  threshold?: number;
}

/** Bundle passed to {@link VirtualizedRows} so callers do not thread each field separately. */
export type VirtualizedListState<T> = {
  virtualRowsData: VirtualRowMeta<T>[];
  shouldVirtualize: boolean;
  virtualItems: VirtualItem[];
  totalSize?: number;
  measureElement: (node: Element | null) => void;
};

export interface UseVirtualizedOptionsReturn<T> extends VirtualizedListState<T> {
  parentRef: RefObject<HTMLElement | null>;
  virtualizedList: VirtualizedListState<T>;
  getOptionKey: (meta: VirtualRowMeta<T>, idx: number) => string;
}

const DEFAULT_THRESHOLD = 80;

const EMPTY_VIRTUAL_ITEMS: VirtualItem[] = [];

export function useVirtualizedOptions<T>(params: UseVirtualizedOptionsParams<T>): UseVirtualizedOptionsReturn<T> {
  const {
    selectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider,
    noCategoryLabel,
    threshold = DEFAULT_THRESHOLD,
  } = params;

  const virtualRowsData: VirtualRowMeta<T>[] = useMemo(() => {
    const rows: VirtualRowMeta<T>[] = [];
    let isFirstCategory = true;
    selectedOptions.forEach((opt, index) => rows.push({ kind: 'selected', option: opt, indexWithinGroup: index }));
    if (includeDivider) rows.push({ kind: 'divider' });
    notSelectedOptions.forEach((opt, index) => {
      const currentCategory = opt.category ?? 'undefined';
      const isFirstInCategory =
        hasCategorizedOptions &&
        categoriesToDisplay.some(cat => cat.category === currentCategory && cat.index === index);
      if (isFirstInCategory) {
        rows.push({
          kind: 'category',
          category: opt.category ?? noCategoryLabel,
          isNoCategory: opt.category == null,
          isFirstCategory,
        });
        isFirstCategory = false;
      }
      rows.push({ kind: 'notSelected', option: opt, indexWithinGroup: index });
    });
    return rows;
  }, [
    selectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider,
    noCategoryLabel,
  ]);

  const shouldVirtualize = virtualRowsData.length > threshold;
  const parentRef = useRef<HTMLElement | null>(null);

  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? virtualRowsData.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const row = virtualRowsData[index];
      if (!row) return 64;
      if (row.kind === 'divider') return 1;
      if (row.kind === 'category') return 40;
      // TreeItem: line-clamp 2 + checkbox + padding — actual height 1 vs 2 lines is corrected via measureElement on option rows only (see VirtualizedRows).
      return 64;
    },
    measureElement: defaultMeasureElement,
    overscan: 8,
  });

  const virtualItemsCandidate = shouldVirtualize ? virtualizer.getVirtualItems() : null;
  const virtualItems = useMemo(() => virtualItemsCandidate ?? EMPTY_VIRTUAL_ITEMS, [virtualItemsCandidate]);
  const totalSize = shouldVirtualize ? virtualizer.getTotalSize() : undefined;

  const measureElement = useCallback(
    (node: Element | null) => {
      virtualizer.measureElement(node);
    },
    [virtualizer],
  );

  const virtualizedList = useMemo(
    (): VirtualizedListState<T> => ({
      virtualRowsData,
      shouldVirtualize,
      virtualItems,
      totalSize,
      measureElement,
    }),
    [virtualRowsData, shouldVirtualize, virtualItems, totalSize, measureElement],
  );

  const getOptionKey = (meta: VirtualRowMeta<T>, idx: number): string => {
    switch (meta.kind) {
      case 'divider':
        return `virtual-divider-${idx}`;
      case 'category':
        return `virtual-category-${idx}-${meta.category}`;
      case 'selected':
      case 'notSelected':
        return `virtual-option-${idx}-${meta.option ? createSelectItemKey(meta.option) : 'unknown'}`;
      default:
        return `virtual-row-${idx}`;
    }
  };

  return {
    virtualRowsData,
    shouldVirtualize,
    parentRef,
    virtualItems,
    totalSize,
    measureElement,
    virtualizedList,
    getOptionKey,
  };
}

export default useVirtualizedOptions;
